import { RoleProtectedRoute } from "@/components/auth/RoleProtectedRoute";
import AdminLayout from "@/layouts/AdminLayout";
import AuthLayout from "@/layouts/AuthLayout";
import LoginPage from "@/pages/auth/LoginPage";
import ForbiddenPage from "@/pages/system/ForbiddenPage";
import NotFoundPage from "@/pages/system/NotFoundPage";
import {
  adminRouteRegistry,
  getDefaultAdminRoute,
} from "@/routes/admin-route-registry";
import { useAuthStore } from "@/stores/authStore";
import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

function ProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((state) => Boolean(state.token));
  const initialized = useAuthStore((state) => state.initialized);
  if (!initialized) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">Securing admin session...</div>;
  }
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function DefaultAdminRoute() {
  const token = useAuthStore((state) => state.token);
  const role = useAuthStore((state) => state.user?.role);
  const initialized = useAuthStore((state) => state.initialized);
  if (!initialized) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">Securing admin session...</div>;
  }
  return (
    <Navigate
      to={token ? getDefaultAdminRoute(role) : "/login"}
      replace
    />
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<DefaultAdminRoute />} />
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/forbidden" element={<ForbiddenPage />} />
        {adminRouteRegistry.map((route) => {
          const Component = route.component;
          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                <RoleProtectedRoute
                  requiredRole={route.requiredRole}
                  allowedRoles={route.allowedRoles}
                >
                  <Component />
                </RoleProtectedRoute>
              }
            />
          );
        })}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="*" element={<DefaultAdminRoute />} />
    </Routes>
  );
}

export default AppRoutes;
